// Project dashboard — fetches /dashboard.json and renders four panels:
// status donut, daily-activity bar, status breakdown list, top contributors.
// Status colors are sourced from the --status-X-accent CSS vars so the
// charts stay consistent with the kanban + badges.
"use strict";

(function () {
  function initDashboard() {
    var root = document.querySelector('.dashboard[data-project-id]');
    if (!root) return;
    if (typeof window.Chart === 'undefined') {
      console.warn('Chart.js not loaded — dashboard charts will be skipped.');
      return;
    }

    var projectId = root.getAttribute('data-project-id');
    var orgSlug   = root.getAttribute('data-org-slug');

    var STATUS_ORDER = ['Backlog', 'Queued', 'In Progress', 'Ready', 'Completed', 'Cancelled'];

    function statusKey(s) {
      return s.toLowerCase().replace(/\s+/g, '-');
    }
    function statusColor(s, variant) {
      // variant: 'accent' | 'text' | 'tint'
      var name = '--status-' + statusKey(s) + '-' + (variant || 'accent');
      var v = getComputedStyle(document.documentElement).getPropertyValue(name);
      return v ? v.trim() : '#888';
    }

    var startInput = document.getElementById('dashboard_start');
    var endInput   = document.getElementById('dashboard_end');
    var applyBtn   = document.getElementById('dashboard_apply');
    var quickpicks = document.querySelectorAll('.dashboard-quickpick');
    var modeButtons = document.querySelectorAll('.dashboard-mode');

    // Filters popover — the funnel icon toggles a floating panel with
    // mode + window + date-range controls. Hidden by default so the
    // charts get the first screen. Click-outside / Escape close.
    var filtersContainer = document.querySelector('[data-dashboard-filters]');
    if (filtersContainer) {
      var filtersTrigger = filtersContainer.querySelector('.dashboard-filters__trigger');
      var filtersPanel = document.getElementById('dashboardFilters');
      if (filtersTrigger && filtersPanel) {
        var openFilters = function () {
          filtersPanel.hidden = false;
          filtersTrigger.setAttribute('aria-expanded', 'true');
        };
        var closeFilters = function () {
          filtersPanel.hidden = true;
          filtersTrigger.setAttribute('aria-expanded', 'false');
        };
        filtersTrigger.addEventListener('click', function (e) {
          e.stopPropagation();
          if (filtersPanel.hidden) openFilters(); else closeFilters();
        });
        document.addEventListener('click', function (e) {
          if (filtersPanel.hidden) return;
          if (!filtersContainer.contains(e.target)) closeFilters();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !filtersPanel.hidden) closeFilters();
        });
      }
    }

    // 'window' (default) — counts transitions INTO each status during the
    // window. 'snapshot' — current state of every task.
    var currentMode = 'window';

    Array.prototype.forEach.call(modeButtons, function (b) {
      b.addEventListener('click', function () {
        var m = b.getAttribute('data-mode');
        if (m === currentMode) return;
        currentMode = m;
        Array.prototype.forEach.call(modeButtons, function (x) {
          x.classList.toggle('is-active', x.getAttribute('data-mode') === currentMode);
        });
        loadAndRender();
      });
    });

    function applyModeHints() {
      var donutHint = document.getElementById('statusPanelHint');
      var listHint  = document.getElementById('breakdownPanelHint');
      var emptyDonut = document.querySelector('#chartStatus').parentNode.parentNode.querySelector('.dashboard-empty');
      if (currentMode === 'window') {
        if (donutHint) donutHint.textContent = 'Transitions into each status during the window.';
        if (listHint)  listHint.textContent  = 'Transitions in window — count + points moved.';
        if (emptyDonut) emptyDonut.textContent = 'No transitions in this window.';
      } else {
        if (donutHint) donutHint.textContent = 'Current state of every task in the project.';
        if (listHint)  listHint.textContent  = 'Counts and points by current status.';
        if (emptyDonut) emptyDonut.textContent = 'No tasks yet.';
      }
    }

    function setRangeDays(days) {
      var today = new Date();
      var start = new Date(today.getTime() - (days - 1) * 86400000);
      startInput.value = start.toISOString().slice(0, 10);
      endInput.value   = today.toISOString().slice(0, 10);
      Array.prototype.forEach.call(quickpicks, function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-days') === String(days));
      });
    }

    Array.prototype.forEach.call(quickpicks, function (b) {
      b.addEventListener('click', function () {
        setRangeDays(parseInt(b.getAttribute('data-days'), 10));
        loadAndRender();
      });
    });
    applyBtn.addEventListener('click', function () {
      Array.prototype.forEach.call(quickpicks, function (q) { q.classList.remove('is-active'); });
      loadAndRender();
    });
    [startInput, endInput].forEach(function (i) {
      i.addEventListener('change', function () {
        Array.prototype.forEach.call(quickpicks, function (q) { q.classList.remove('is-active'); });
      });
    });

    if (!startInput.value || !endInput.value) setRangeDays(30);

    function fetchMetrics() {
      var qs = new URLSearchParams();
      if (startInput.value) qs.set('start', startInput.value);
      if (endInput.value)   qs.set('end',   endInput.value);
      qs.set('mode', currentMode);
      var url = '/org/' + orgSlug + '/projects/' + projectId + '/dashboard.json?' + qs.toString();
      return fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('dashboard load failed: ' + res.status);
        return res.json();
      });
    }

    var statusChart = null;
    var dailyChart = null;

    function renderStatusDonut(data) {
      var canvas = document.getElementById('chartStatus');
      var emptyEl = canvas.parentNode.parentNode.querySelector('.dashboard-empty');

      var labels = STATUS_ORDER.filter(function (s) { return (data.statusCounts[s] || 0) > 0; });
      var counts = labels.map(function (s) { return data.statusCounts[s]; });
      var colors = labels.map(function (s) { return statusColor(s, 'accent'); });

      if (labels.length === 0) {
        if (statusChart) { statusChart.destroy(); statusChart = null; }
        canvas.style.display = 'none';
        emptyEl.hidden = false;
        return;
      }
      canvas.style.display = '';
      emptyEl.hidden = true;

      if (statusChart) statusChart.destroy();
      statusChart = new window.Chart(canvas, {
        type: 'doughnut',
        data: { labels: labels, datasets: [{
          data: counts,
          backgroundColor: colors,
          borderColor: '#fff',
          borderWidth: 2
        }]},
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '62%',
          plugins: {
            legend: { position: 'right', labels: { font: { family: 'Nunito', size: 12 } } },
            tooltip: { callbacks: {
              label: function (ctx) {
                var s = ctx.label;
                var n = ctx.parsed;
                var pts = data.statusPoints[s] || 0;
                var noun = data.mode === 'window' ? 'entered' : (n === 1 ? 'task' : 'tasks');
                var label = data.mode === 'window'
                  ? n + ' ' + noun + (pts > 0 ? ' · ' + pts + ' pts' : '')
                  : n + ' ' + noun + (pts > 0 ? ' · ' + pts + ' pts' : '');
                return label;
              }
            }}
          }
        }
      });
    }

    function renderDailyActivity(data) {
      var canvas = document.getElementById('chartDaily');
      var emptyEl = canvas.parentNode.parentNode.querySelector('.dashboard-empty');

      var rows = data.daily || [];
      var anyActivity = rows.some(function (r) { return r.created > 0 || r.completed > 0; });
      if (rows.length === 0 || !anyActivity) {
        if (dailyChart) { dailyChart.destroy(); dailyChart = null; }
        canvas.style.display = 'none';
        emptyEl.hidden = false;
        return;
      }
      canvas.style.display = '';
      emptyEl.hidden = true;

      var labels = rows.map(function (r) {
        var d = new Date(r.date + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      var created = rows.map(function (r) { return r.created; });
      var completed = rows.map(function (r) { return r.completed; });

      if (dailyChart) dailyChart.destroy();
      dailyChart = new window.Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Created',
              data: created,
              backgroundColor: statusColor('Backlog', 'accent'),
              borderColor: statusColor('Backlog', 'text'),
              borderWidth: 1
            },
            {
              label: 'Completed',
              data: completed,
              backgroundColor: statusColor('Completed', 'accent'),
              borderColor: statusColor('Completed', 'text'),
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { font: { family: 'Nunito', size: 12 } } }
          },
          scales: {
            x: { stacked: false, ticks: { font: { family: 'Nunito', size: 10 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
            y: { beginAtZero: true, ticks: { font: { family: 'Nunito', size: 10 }, precision: 0 } }
          }
        }
      });
    }

    function renderStatusBreakdown(data) {
      var ul = document.getElementById('statusBreakdownList');
      ul.innerHTML = '';
      STATUS_ORDER.forEach(function (s) {
        var n   = data.statusCounts[s] || 0;
        var pts = data.statusPoints[s] || 0;
        var li = document.createElement('li');
        li.className = 'dashboard-list__row';
        var swatch = document.createElement('span');
        swatch.className = 'dashboard-list__swatch';
        swatch.style.background = statusColor(s, 'accent');
        var label = document.createElement('span');
        label.className = 'dashboard-list__label';
        label.textContent = s;
        var meta = document.createElement('span');
        meta.className = 'dashboard-list__meta';
        var noun = data.mode === 'window' ? (n === 1 ? 'entered' : 'entered') : (n === 1 ? 'task' : 'tasks');
        meta.textContent = n + ' ' + noun + (pts > 0 ? ' · ' + pts + ' pts' : '');
        li.appendChild(swatch);
        li.appendChild(label);
        li.appendChild(meta);
        ul.appendChild(li);
      });
    }

    function renderContributors(data) {
      var tbody = document.querySelector('#contributorsTable tbody');
      tbody.innerHTML = '';
      var rows = data.byAssignee || [];
      if (rows.length === 0) {
        var tr = document.createElement('tr');
        tr.innerHTML = '<td colspan="4" class="muted">No assigned tasks yet.</td>';
        tbody.appendChild(tr);
        return;
      }
      rows.slice(0, 10).forEach(function (r) {
        var tr = document.createElement('tr');
        var nameTd = document.createElement('td');
        nameTd.textContent = r.label + (r.kind === 'agent' ? ' (Agent)' : '');
        if (r.kind === 'unassigned') nameTd.className = 'muted';
        var openTd = document.createElement('td'); openTd.className = 'num'; openTd.textContent = r.openCount;
        var doneTd = document.createElement('td'); doneTd.className = 'num'; doneTd.textContent = r.completedInWindow;
        var ptsTd  = document.createElement('td'); ptsTd.className = 'num';
        ptsTd.textContent = r.pointsCompletedInWindow > 0 ? r.pointsCompletedInWindow : '—';
        tr.appendChild(nameTd); tr.appendChild(openTd); tr.appendChild(doneTd); tr.appendChild(ptsTd);
        tbody.appendChild(tr);
      });
    }

    function loadAndRender() {
      applyModeHints();
      fetchMetrics()
        .then(function (data) {
          renderStatusDonut(data);
          renderDailyActivity(data);
          renderStatusBreakdown(data);
          renderContributors(data);
        })
        .catch(function (err) {
          console.error(err);
          var ul = document.getElementById('statusBreakdownList');
          if (ul) ul.innerHTML = '<li class="dashboard-list__row muted">Could not load metrics: ' + err.message + '</li>';
        });
    }

    loadAndRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDashboard);
  } else {
    initDashboard();
  }
})();
